import PropTypes from 'prop-types';

import { SCOPES } from '@techchange/components';

import {
	ENUMS_VIDEO,
	MUX_VIDEO_MASTER_ACCESS,
	MUX_VIDEO_MASTER_STATUS,
} from '../constants';

/**
 * A video from our Library.
 */
const video = PropTypes.shape({

	/**
	 * The unique identifier of the video
	 */
	id: PropTypes.string.isRequired,

	/**
	 * The provider for the video. Currently only supports Mux.
	 */
	provider: PropTypes.oneOf([
		ENUMS_VIDEO.PROVIDER.MUX,
	]).isRequired,

	/**
	 * The external video provider's ID
	 */
	provider_id: PropTypes.string.isRequired,

	/**
     * The latest status of the video.
     *
     * The video will be "waiting" when an upload has been initiated but the direct upload
     * has yet to be processed.
     *
     * The video will be "preparing" while the direct upload has been processed and the provider
     * is still encoding the video.
     *
     * The video will be "ready" once the upload is processed and encoded. The video can be
     * played at this time and will have playback IDs assigned.
	 */
	status: PropTypes.oneOf([
		ENUMS_VIDEO.STATUS.DELETED,
		ENUMS_VIDEO.STATUS.ERRORED,
		ENUMS_VIDEO.STATUS.PREPARING,
		ENUMS_VIDEO.STATUS.READY,
		ENUMS_VIDEO.STATUS.WAITING,
	]).isRequired,

	/**
     * The ID of the signed playback policy. This will be null while the video is awaiting
     * upload and will be populated when the video is ready for playback.
	 */
	playback_id_signed: PropTypes.string,

	/**
     * The ID of the public playback policy. Only returned if requested and user is admin.
     * This will be null while the video is awaiting upload and will be populated when the
     * video is ready for playback.
	 */
	playback_id_public: PropTypes.string,

	/**
	 * The timestamp (in seconds) from the video to use
	 * for a custom thumbnail, if any.
	 */
	thumbnail_timestamp: PropTypes.number,

	/**
	 * The type of scope for the video
	 */
	scope: PropTypes.oneOf([
		SCOPES.SESSION,
		SCOPES.ORGANIZATION,
	]).isRequired,

	/**
	 * The scope ID for the video
	 */
	scope_id: PropTypes.string.isRequired,

	/**
	 * The organization ID for the video
	 */
	organization_id: PropTypes.string.isRequired,

	/**
	 * The JWTs for secure playback
	 */
	mux_tokens: PropTypes.shape({
		video: PropTypes.string.isRequired,
		thumbnail: PropTypes.string.isRequired,
		storyboard: PropTypes.string.isRequired,
	}),

	/**
	 * The master access property options for requesting
	 * master access to an asset. Only defined for admins.
	 */
	master_access: PropTypes.oneOf(
		Object.values(MUX_VIDEO_MASTER_ACCESS)
	),

	/**
	 * An object containing the current status of Master Access
	 * and the link to the Master MP4 file when ready. This object
	 * does not exist if master_access is set to none and when the
	 * temporary URL expires. Only defined for admins.
	 */
	master: PropTypes.shape({

		/**
		 * The status of the master asset
		 */
		status: PropTypes.oneOf(
			Object.values(MUX_VIDEO_MASTER_STATUS)
		).isRequired,

		/**
		 * The temporary URL to the master version of the video, as an
		 * MP4 file. This URL will expire 24 hours after creation.
		 */
		url: PropTypes.string,

	})

});

export default {
	video
};
